import { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/navbar/index.jsx';
import Footer from '../../components/Footer/Footer.js';

/* ── Intersection Observer Hook ── */
const useInView = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

const Reveal = ({ children, delay = 0, className = "", style = {} }: any) => {
  const { ref, inView } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(44px)",
      transition: `opacity 0.85s cubic-bezier(.16,1,.3,1) ${delay}ms, transform 0.85s cubic-bezier(.16,1,.3,1) ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
};

/* ── Aurora Canvas ── */
const AuroraCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouse = useRef({ x: 0, y: 0 });
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    const onMove = (e: MouseEvent) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("mousemove", onMove);

    const pts = Array.from({ length: 90 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.35, vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.6 + 0.3, a: Math.random() * 0.55 + 0.1,
    }));

    const blobs = [
      { x: 0.15, y: 0.25, r: 0.45, h: 195, ph: 0, sp: 0.0025 },
      { x: 0.75, y: 0.55, r: 0.38, h: 210, ph: 2.1, sp: 0.003 },
      { x: 0.5,  y: 0.1,  r: 0.32, h: 185, ph: 4,   sp: 0.002 },
      { x: 0.88, y: 0.3,  r: 0.28, h: 200, ph: 1.2, sp: 0.004 },
      { x: 0.3,  y: 0.75, r: 0.25, h: 220, ph: 3,   sp: 0.0035 },
    ];

    let t = 0;
    const draw = () => {
      t += 0.007;
      ctx.clearRect(0, 0, W, H);

      // Deep navy base
      const bg = ctx.createLinearGradient(0, 0, W, H);
      bg.addColorStop(0, "#020c18");
      bg.addColorStop(0.5, "#030f20");
      bg.addColorStop(1, "#020c18");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // Aurora blobs
      blobs.forEach(b => {
        const bx = (b.x + Math.sin(t * b.sp * 200 + b.ph) * 0.09) * W;
        const by = (b.y + Math.cos(t * b.sp * 200 + b.ph * 0.7) * 0.07) * H;
        const gr = ctx.createRadialGradient(bx, by, 0, bx, by, b.r * Math.min(W, H));
        gr.addColorStop(0, `hsla(${b.h},85%,55%,0.14)`);
        gr.addColorStop(0.4, `hsla(${b.h},75%,40%,0.07)`);
        gr.addColorStop(1, "transparent");
        ctx.fillStyle = gr;
        ctx.fillRect(0, 0, W, H);
      });

      // Mouse glow
      const mg = ctx.createRadialGradient(mouse.current.x, mouse.current.y, 0, mouse.current.x, mouse.current.y, 220);
      mg.addColorStop(0, "rgba(56,189,248,0.08)");
      mg.addColorStop(1, "transparent");
      ctx.fillStyle = mg;
      ctx.fillRect(0, 0, W, H);

      // Grid
      ctx.strokeStyle = "rgba(56,189,248,0.04)";
      ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 70) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
      for (let y = 0; y < H; y += 70) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

      // Particles + connections
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(125,211,252,${p.a})`; ctx.fill();
      });
      pts.forEach((a, i) => pts.slice(i + 1).forEach(b => {
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 110) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(56,189,248,${0.13 * (1 - d / 110)})`; ctx.lineWidth = 0.5; ctx.stroke();
        }
      }));

      // Sweep line
      const sy = (Math.sin(t * 0.35) * 0.5 + 0.5) * H;
      const sg = ctx.createLinearGradient(0, sy - 80, 0, sy + 80);
      sg.addColorStop(0, "transparent"); sg.addColorStop(0.5, "rgba(56,189,248,0.04)"); sg.addColorStop(1, "transparent");
      ctx.fillStyle = sg; ctx.fillRect(0, sy - 80, W, 160);

      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); window.removeEventListener("mousemove", onMove); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }} />;
};

/* ── CountUp ── */
const CountUp = ({ to, suffix = "" }: { to: number; suffix?: string }) => {
  const [v, setV] = useState(0);
  const { ref, inView } = useInView();
  useEffect(() => {
    if (!inView) return;
    let n = 0; const step = to / 50;
    const id = setInterval(() => { n += step; if (n >= to) { setV(to); clearInterval(id); } else setV(Math.floor(n)); }, 20);
    return () => clearInterval(id);
  }, [inView, to]);
  return <span ref={ref}>{v}{suffix}</span>;
};

/* ── Main Component ── */
export default function Roadmap() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [form, setForm] = useState({ name: "", designation: "", company: "", website: "", phone: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToForm = () => {
    document.querySelector('.form-sec')?.scrollIntoView({ behavior: 'smooth' });
  };

  const steps = [
    { num: "01", title: "Diagnostic Review", icon: "🔍", desc: "We assess your current financial health and capital positioning — identifying gaps, risks, and investor-readiness across 12 critical dimensions." },
    { num: "02", title: "Financial Architecture", icon: "📐", desc: "We build structured projections, capital strategy, and valuation logic — engineered to institutional standards that withstand investor scrutiny." },
    { num: "03", title: "Readiness Optimization", icon: "⚙️", desc: "We align compliance, audit documentation, governance structure, and investor presentation materials to due-diligence requirements." },
    { num: "04", title: "Execution & Support", icon: "🚀", desc: "We assist in implementing the financial roadmap and preparing your company for serious capital discussions with qualified investors." },
  ];

  const faqs = [
    { q: "Do you guarantee funding?", a: "No consultant can ethically guarantee funding. We guarantee structured preparation and investor readiness — the foundation that makes capital conversations succeed." },
    { q: "Is this suitable for loss-making companies?", a: "Yes, provided there is revenue visibility and a scalable business model. We assess each company individually based on growth potential." },
    { q: "Can the ₹60,000 be adjusted in future engagement?", a: "In select cases, yes — subject to mandate discussion." },
    { q: "Do you work across industries?", a: "Yes. We support manufacturing, tech, services, healthcare, and emerging sectors." },
  ];

  const fundingServices = [
    "Funding Support",
    "Debt Structuring", 
    "Angel Investors",
    "Pre IPO Raise",
    "Foreign Funding",
    "Project Funding"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Replace with your Google Apps Script Web App URL
      const scriptURL = 'https://script.google.com/macros/s/AKfycbxrF7gsBRvu5QHwBXDJ6siDa0bbgCTVGB9iBxVVa2pdZHam1QvublupL8wP6sTYC9bmTg/exec';
      
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('designation', form.designation);
      formData.append('company', form.company);
      formData.append('website', form.website);
      formData.append('phone', form.phone);
      formData.append('email', form.email);
      formData.append('timestamp', new Date().toISOString());

      const response = await fetch(scriptURL, {
        method: 'POST',
        body: formData
      });

      // Navigate to thank you page
      navigate('/thankyou');
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Navbar />
    <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", background: "#020c18", color: "#fff", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..60,300;12..60,400;12..60,500;12..60,600;12..60,700;12..60,800&family=Instrument+Serif:ital@0;1&display=swap');

        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        :root{
          --blue:#38bdf8; --blue2:#0ea5e9; --blue3:#7dd3fc;
          --teal:#14b8a6; --teal2:#0d9488; --teal3:#2dd4bf;
          /* Dark backgrounds — noticeably lighter than before */
          --navy:#132232; --navy2:#172a3a; --card:#1e3548; --cardb:#243f56;
          --border:rgba(45,212,191,0.18); --border2:rgba(45,212,191,0.25);
          --muted:rgba(255,255,255,0.7); --white:#fff;
        }
        html{scroll-behavior:smooth;}
        button,input,select{font-family:inherit;cursor:pointer;}
        input,select{cursor:text;}

        /* ── NAV ── */
        .nav{
          position:fixed;top:0;left:0;right:0;z-index:200;height:68px;
          display:flex;align-items:center;justify-content:space-between;padding:0 5%;
          background:rgba(13,31,45,0.88);backdrop-filter:blur(22px) saturate(1.5);
          border-bottom:1px solid var(--border2);
        }
        .nlogo{display:flex;align-items:center;gap:10px;text-decoration:none;}
        .nbadge{width:36px;height:36px;border-radius:8px;background:linear-gradient(135deg,var(--teal2),var(--blue2));display:grid;place-items:center;font-weight:800;font-size:13px;color:#fff;box-shadow:0 0 20px rgba(20,184,166,0.35);}
        .nname{font-size:15px;font-weight:700;color:#fff;}
        .nsub{font-size:10px;color:var(--muted);}
        .nlinks{display:flex;gap:28px;list-style:none;}
        .nlinks a{font-size:12px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);text-decoration:none;transition:color .2s;}
        .nlinks a:hover{color:var(--teal3);}
        .ncta{background:linear-gradient(135deg,var(--teal2),var(--blue2));border:none;color:#fff;padding:9px 22px;border-radius:6px;font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;transition:all .2s;box-shadow:0 0 20px rgba(20,184,166,0.25);}
        .ncta:hover{transform:translateY(-1px);box-shadow:0 0 32px rgba(20,184,166,0.5);}
        .hbg{display:none;background:none;border:none;flex-direction:column;gap:5px;padding:4px;}
        .hbg span{display:block;width:20px;height:1.5px;background:#fff;transition:.3s;}
        .mob{position:fixed;top:68px;inset-x:0;z-index:199;background:rgba(13,31,45,.97);backdrop-filter:blur(20px);border-bottom:1px solid var(--border2);padding:28px 5%;display:flex;flex-direction:column;gap:20px;}
        .mob a{font-size:14px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);text-decoration:none;}

        /* ── HERO ── */
        .hero{position:relative;min-height:80vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:80px 5% 60px;overflow:hidden;background:var(--navy);}
        .hcont{position:relative;z-index:2;max-width:840px;}
        .eyebrow{display:inline-flex;align-items:center;gap:14px;border:1px solid rgba(45,212,191,0.4);padding:12px 36px;margin-bottom:24px;background:rgba(45,212,191,0.08);animation:up .8s cubic-bezier(.16,1,.3,1) .1s both;}
        .edot{width:8px;height:8px;background:var(--teal3);border-radius:50%;animation:blink 2s infinite;}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}
        .etxt{font-size:13px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:var(--teal3);}
        .h1{font-size:clamp(2.4rem,6vw,5.5rem);font-weight:800;line-height:.95;letter-spacing:-.03em;margin-bottom:8px;animation:up .9s cubic-bezier(.16,1,.3,1) .2s both;color:#fff;}
        .h1 .it{font-family:'Instrument Serif',serif;font-style:italic;color:var(--teal3);font-weight:400;}
        .htag{font-size:clamp(.8rem,1.3vw,0.9rem);color:rgba(255,255,255,.65);letter-spacing:.3em;text-transform:uppercase;margin-bottom:16px;animation:up .9s cubic-bezier(.16,1,.3,1) .35s both;}
        .hdesc{font-size:clamp(0.9rem,1.5vw,1rem);color:rgba(255,255,255,.75);line-height:1.6;max-width:560px;margin:0 auto 16px;font-weight:300;animation:up .9s cubic-bezier(.16,1,.3,1) .45s both;}
        .hbtns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;animation:up .9s cubic-bezier(.16,1,.3,1) .6s both;}
        
        /* Funding Services Tags */
        .funding-tags {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          justify-content: center;
          max-width: 600px;
          margin: 24px auto 28px;
          animation: up .9s cubic-bezier(.16,1,.3,1) .5s both;
        }
        .funding-tag {
          background: rgba(45,212,191,0.08);
          border: 1px solid rgba(45,212,191,0.25);
          padding: 8px 18px;
          border-radius: 30px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
          color: var(--teal3);
          transition: all 0.25s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          backdrop-filter: blur(4px);
        }
        .funding-tag:hover {
          background: var(--teal3);
          color: #020c18;
          transform: translateY(-2px);
          border-color: var(--teal3);
          box-shadow: 0 8px 20px rgba(45,212,191,0.25);
        }

        @keyframes up{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:translateY(0)}}

        .btng{background:linear-gradient(135deg,var(--teal2),var(--blue2));border:none;color:#fff;padding:12px 28px;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;transition:all .25s;box-shadow:0 0 40px rgba(20,184,166,.3),inset 0 1px 0 rgba(255,255,255,.12);display:inline-flex;align-items:center;gap:8px;}
        .btng:hover{transform:translateY(-2px);box-shadow:0 0 60px rgba(20,184,166,.55);}
        .btno{background:transparent;border:1px solid rgba(255,255,255,.28);color:rgba(255,255,255,.85);padding:12px 28px;font-size:12px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;transition:all .25s;display:inline-flex;align-items:center;gap:8px;}
        .btno:hover{border-color:var(--teal3);color:var(--teal3);transform:translateY(-2px);}

        .hscroll{position:absolute;bottom:20px;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:4px;animation:up 1s .9s both;}
        .sctxt{font-size:9px;letter-spacing:.2em;text-transform:uppercase;color:rgba(255,255,255,.5);}
        .sctrk{width:1px;height:40px;background:rgba(45,212,191,.25);position:relative;overflow:hidden;}
        .scfill{position:absolute;top:0;left:0;right:0;height:20px;background:linear-gradient(to bottom,var(--teal3),transparent);animation:sdrop 2s ease-in-out infinite;}
        @keyframes sdrop{0%{top:-20px}100%{top:40px}}

        /* ── MARQUEE ── */
        .mq{overflow:hidden;border-top:1px solid var(--border2);border-bottom:1px solid var(--border2);background:var(--card);padding:12px 0;}
        .mqt{display:flex;animation:mq 30s linear infinite;width:max-content;}
        .mqi{display:flex;align-items:center;gap:12px;padding:0 28px;font-size:10px;font-weight:600;letter-spacing:.15em;text-transform:uppercase;color:rgba(255,255,255,.6);white-space:nowrap;}
        .mqd{width:4px;height:4px;background:var(--teal3);border-radius:50%;}
        @keyframes mq{from{transform:translateX(0)}to{transform:translateX(-50%)}}

        /* ── STATS ── */
        .stats{display:grid;grid-template-columns:repeat(4,1fr);background:var(--cardb);border-bottom:1px solid var(--border2);}
        .stat{padding:28px 24px;border-right:1px solid var(--border2);position:relative;overflow:hidden;transition:background .3s;}
        .stat:last-child{border-right:none;}
        .stat:hover{background:rgba(45,212,191,0.08);}
        .statn{font-size:clamp(1.8rem,2.8vw,2.5rem);font-weight:800;color:var(--teal3);line-height:1;margin-bottom:4px;}
        .statl{font-size:10px;font-weight:500;letter-spacing:.15em;text-transform:uppercase;color:rgba(255,255,255,.65);}
        .statbar{position:absolute;bottom:0;left:0;right:0;height:2px;background:linear-gradient(90deg,var(--teal),var(--blue2));transform:scaleX(0);transform-origin:left;transition:transform .5s cubic-bezier(.16,1,.3,1);}
        .stat:hover .statbar{transform:scaleX(1);}

        /* ── COMMONS ── */
        .wrap{max-width:1280px;margin:0 auto;padding:0 5%;}
        .sec{padding:60px 0;} /* REDUCED from 120px to 60px */
        /* dark sections — background explicitly set */
        .secdark{background:var(--navy);}
        .secalt{background:var(--navy2);}
        /* white/light sections */
        .secw{background:#edfafa;}
        .secw2{background:#f0f9ff;}
        /* shared label */
        .lbl{font-size:10px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:var(--teal3);margin-bottom:12px;display:flex;align-items:center;gap:8px;}
        .lbl::before{content:'';height:1px;background:linear-gradient(90deg,transparent,var(--teal3));width:30px;}
        /* white section label override */
        .secw .lbl,.secw2 .lbl{color:#0a6e62;}
        .secw .lbl::before,.secw2 .lbl::before{background:linear-gradient(90deg,transparent,#0a6e62);}
        /* headings */
        .h2{font-size:clamp(1.8rem,3.8vw,3rem);font-weight:800;line-height:1.05;letter-spacing:-.025em;color:#fff;}
        .h2 .it{font-family:'Instrument Serif',serif;font-style:italic;color:var(--teal3);font-weight:400;}
        .secw .h2,.secw2 .h2{color:#0b1f2a;}
        .secw .h2 .it,.secw2 .h2 .it{color:#0a6e62;}
        /* body muted */
        .muted{color:rgba(255,255,255,.75);font-size:0.95rem;line-height:1.7;font-weight:300;}
        .secw .muted,.secw2 .muted{color:#1c3d4a;font-weight:400;}

        /* ── SPLIT LAYOUT FOR FAIL SECTION ── */
        .fail-split{display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:start;}
        .fail-left{padding-right:30px;border-right:2px solid rgba(10,110,98,0.25);}
        .fail-right{padding-left:30px;}
        
        /* ── PAIN (Left Side) ── */
        .pgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:1px;margin-top:36px;background:rgba(10,110,98,0.2);border:1px solid rgba(10,110,98,0.2);}
        .pcell{background:#edfafa;padding:22px 20px;transition:background .3s;}
        .pcell:hover{background:#cff6f0;}
        .px{font-size:18px;margin-bottom:8px;color:#b91c1c;font-weight:700;}
        .pt{font-size:13px;color:#082030;line-height:1.5;font-weight:600;}
        .pfail{margin-top:28px;text-align:center;font-size:0.95rem;font-style:italic;color:#086b5f;font-weight:700;border-top:1px solid rgba(10,110,98,0.22);padding-top:24px;}

        /* ── DIFFERENT SECTION (Right Side) - Enhanced styling for visibility ── */
        .different-heading {
          font-size: 2rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 24px;
          color: #0b1f2a;
        }
        .different-heading span {
          font-family: 'Instrument Serif', serif;
          font-style: italic;
          color: #0a6e62;
          font-weight: 400;
        }
        .qcard-right {
          border: 1px solid rgba(10,110,98,0.3);
          background: linear-gradient(135deg, rgba(10,110,98,0.12), rgba(56,189,248,0.08));
          padding: 32px;
          margin-top: 12px;
          margin-bottom: 24px;
          position: relative;
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(10,110,98,0.15);
        }
        .qcard-right::before {
          content: '"';
          position: absolute;
          top: -10px;
          left: 24px;
          font-size: 80px;
          color: #0a6e62;
          opacity: 0.25;
          font-family: 'Instrument Serif', serif;
          line-height: 1;
        }
        .qtxt-right {
          font-family: 'Instrument Serif', serif;
          font-size: 1.3rem;
          color: #0b1f2a;
          line-height: 1.5;
          font-style: italic;
          font-weight: 500;
          position: relative;
          z-index: 2;
        }
        .execution-quote {
          border-left: 4px solid #0a6e62;
          background: rgba(10,110,98,0.06);
          padding: 24px 28px;
          margin: 28px 0 24px;
          border-radius: 0 8px 8px 0;
        }
        .execution-quote p {
          font-size: 1.1rem;
          font-weight: 600;
          color: #0b1f2a;
          line-height: 1.6;
          font-family: 'Instrument Serif', serif;
          font-style: italic;
        }
        .execution-quote span {
          display: block;
          margin-top: 8px;
          font-size: 0.9rem;
          font-weight: 500;
          color: #0a6e62;
          font-family: 'Bricolage Grotesque', sans-serif;
          font-style: normal;
        }
        .notgeneric-right {
          margin: 24px 0;
          border-left: 3px solid #0a6e62;
          padding-left: 20px;
          background: rgba(255,255,255,0.5);
          padding: 16px 20px;
          border-radius: 0 8px 8px 0;
        }
        .notgeneric-right strong {
          color: #0b1f2a;
          display: block;
          font-size: 15px;
          margin-bottom: 6px;
          font-weight: 800;
        }
        .notgeneric-right p {
          font-size: 14px;
          color: #1c3d4a;
          line-height: 1.6;
          font-weight: 500;
        }
        .tags-right {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 28px;
        }
        .tg-right {
          border: 1px solid rgba(10,110,98,0.4);
          background: rgba(10,110,98,0.08);
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
          color: #0a6e62;
          border-radius: 30px;
          transition: all 0.25s ease;
        }
        .tg-right:hover {
          background: #0a6e62;
          color: white;
          border-color: #0a6e62;
          transform: translateY(-2px);
        }

        /* ── FULL WIDTH PROGRAM SECTION (REDESIGNED) ── */
        .program-full {
          max-width: 900px;
          margin: 0 auto;
        }

        .program-points {
          margin-top: 32px;
        }

        .program-points-label {
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--teal3);
          margin-bottom: 24px;
          position: relative;
          display: inline-block;
          padding-bottom: 8px;
          border-bottom: 2px solid rgba(45, 212, 191, 0.3);
        }

        .program-points-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .program-point-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px 18px;
          background: linear-gradient(135deg, rgba(45, 212, 191, 0.05), rgba(56, 189, 248, 0.02));
          border: 1px solid rgba(45, 212, 191, 0.15);
          border-radius: 8px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          backdrop-filter: blur(4px);
        }

        .program-point-item:hover {
          transform: translateX(6px);
          background: linear-gradient(135deg, rgba(45, 212, 191, 0.12), rgba(56, 189, 248, 0.05));
          border-color: rgba(45, 212, 191, 0.4);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }

        .program-point-icon {
          font-size: 18px;
          font-weight: 700;
          color: var(--teal3);
          line-height: 1.2;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }

        .program-point-item:hover .program-point-icon {
          transform: translateX(4px);
        }

        .program-point-text {
          font-size: 14px;
          color: var(--muted);
          line-height: 1.5;
          font-weight: 400;
          transition: color 0.3s ease;
        }

        .program-point-item:hover .program-point-text {
          color: rgba(255, 255, 255, 0.95);
        }

        /* Responsive adjustments for program points */
        @media (max-width: 768px) {
          .program-points-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .program-point-item {
            padding: 14px 16px;
          }
          
          .program-point-text {
            font-size: 13px;
          }
        }

        @media (max-width: 480px) {
          .program-point-item {
            padding: 12px 14px;
          }
        }

        /* ── REASONS ── */
        .rg{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:rgba(10,110,98,0.18);border:1px solid rgba(10,110,98,0.18);margin-top:40px;}
        .rc{background:#edfafa;padding:28px 24px;position:relative;overflow:hidden;transition:background .3s;}
        .rc::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--teal2),var(--blue2));transform:scaleX(0);transform-origin:left;transition:transform .4s cubic-bezier(.16,1,.3,1);}
        .rc:hover{background:#cff6f0;}
        .rc:hover::after{transform:scaleX(1);}
        .ri{font-size:24px;margin-bottom:12px;display:block;}
        .rt{font-size:15px;font-weight:700;margin-bottom:6px;color:#061820;}
        .rd{font-size:12px;color:#0a2d3f;line-height:1.6;font-weight:500;}

        /* ── PLANS ── */
        .plans{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:40px;}
        .plan{border:1px solid var(--border);padding:36px 32px;position:relative;overflow:hidden;transition:transform .3s,box-shadow .3s;}
        .plan:hover{transform:translateY(-6px);box-shadow:0 30px 60px rgba(0,0,0,.4);}
        .plan-s{background:var(--card);}
        .plan-p{background:linear-gradient(160deg,var(--cardb),var(--card));border-color:rgba(45,212,191,.4);}
        .plan-p::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,var(--teal),var(--blue2),transparent);}
        .rib{position:absolute;top:20px;right:-30px;background:linear-gradient(135deg,var(--teal2),var(--blue2));color:#fff;font-size:8px;font-weight:800;letter-spacing:.15em;text-transform:uppercase;padding:4px 40px;transform:rotate(45deg);}
        .ptag{font-size:10px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:var(--teal3);margin-bottom:8px;}
        .pname{font-size:1.3rem;font-weight:800;margin-bottom:6px;letter-spacing:-.01em;color:#fff;}
        .pdesign{font-size:12px;color:rgba(255,255,255,.7);margin-bottom:10px;font-style:italic;}
        .pprice{font-size:2.4rem;font-weight:800;color:var(--teal3);line-height:1;margin-bottom:4px;}
        .pprice sup{font-size:1.1rem;font-weight:600;vertical-align:super;}
        .pnote{font-size:11px;color:rgba(255,255,255,.65);margin-bottom:20px;padding-bottom:20px;border-bottom:1px solid rgba(255,255,255,.1);}
        .pwhat{font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--teal3);margin-bottom:12px;}
        .pf{list-style:none;display:flex;flex-direction:column;gap:8px;margin-bottom:16px;}
        .pf li{font-size:13px;color:rgba(255,255,255,.82);display:flex;align-items:flex-start;gap:8px;line-height:1.4;}
        .pideal{margin-top:16px;padding-top:16px;border-top:1px solid rgba(255,255,255,.1);margin-bottom:24px;}
        .pideal-l{font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--teal3);margin-bottom:8px;}
        .pideal ul{list-style:none;display:flex;flex-direction:column;gap:6px;}
        .pideal ul li{font-size:12px;color:rgba(255,255,255,.75);display:flex;align-items:center;gap:6px;}
        .pideal ul li::before{content:'→';color:var(--teal3);font-weight:700;}
        .pb{width:100%;padding:12px;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;transition:all .25s;display:flex;align-items:center;justify-content:center;gap:6px;}
        .pb-p{background:linear-gradient(135deg,var(--teal2),var(--blue2));border:none;color:#fff;box-shadow:0 0 30px rgba(20,184,166,.25);}
        .pb-p:hover{box-shadow:0 0 50px rgba(20,184,166,.5);}
        .pb-s{background:transparent;border:1px solid rgba(255,255,255,.25);color:rgba(255,255,255,.85);}
        .pb-s:hover{border-color:var(--teal3);color:var(--teal3);}

        /* ── STEPS ── */
        .sw{display:grid;grid-template-columns:280px 1fr;gap:40px;margin-top:40px;align-items:start;}
        .snav{display:flex;flex-direction:column;gap:2px;}
        .sb{text-align:left;background:none;border:none;border-left:2px solid transparent;padding:16px 20px;color:#0e3347;font-size:13px;font-weight:600;transition:all .2s;display:flex;align-items:center;gap:12px;width:100%;}
        .sb.on{border-left-color:#086b5f;color:#061820;background:rgba(8,107,95,.1);}
        .sb:hover:not(.on){border-left-color:rgba(8,107,95,.4);color:#061820;}
        .sn{font-size:10px;font-weight:700;letter-spacing:.1em;color:#086b5f;width:24px;flex-shrink:0;}
        .sdp{border:1px solid rgba(8,107,95,0.22);background:linear-gradient(140deg,#d4f5ee,#e4f6fb);padding:40px;position:relative;overflow:hidden;min-height:300px;display:flex;flex-direction:column;justify-content:center;}
        .sdp::before{content:'';position:absolute;top:0;right:0;width:220px;height:220px;background:radial-gradient(circle,rgba(8,107,95,.12),transparent 70%);pointer-events:none;}
        .sbig{font-size:6rem;font-weight:800;color:rgba(8,107,95,.1);line-height:1;position:absolute;bottom:10px;right:20px;}
        .sico{font-size:28px;margin-bottom:14px;}
        .stit{font-size:1.5rem;font-weight:800;color:#086b5f;margin-bottom:10px;letter-spacing:-.01em;}
        .sdesc{font-size:14px;color:#082030;line-height:1.7;font-weight:500;max-width:440px;}

        /* ── WHO ── */
        .wg{display:grid;grid-template-columns:1.1fr 1fr;gap:60px;align-items:start;}
        .wcs{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:28px;}
        .wc{border:1px solid rgba(8,107,95,0.28);padding:14px;font-size:12px;color:#082030;background:#edfafa;display:flex;align-items:center;gap:8px;line-height:1.4;transition:all .25s;font-weight:600;}
        .wc:hover{border-color:#086b5f;color:#061820;background:#cff6f0;}
        .wd{width:5px;height:5px;border-radius:50%;background:#086b5f;flex-shrink:0;}
        .wyp{background:linear-gradient(160deg,#d4f5ee,#e4f6fb);border:1px solid rgba(8,107,95,.28);padding:36px;position:relative;overflow:hidden;}
        .wyp::after{content:'';position:absolute;bottom:0;right:0;width:140px;height:140px;background:radial-gradient(circle,rgba(8,107,95,.1),transparent);pointer-events:none;}
        .wyl{list-style:none;display:flex;flex-direction:column;gap:10px;margin-top:20px;}
        .wyl li{font-size:13px;color:#082030;display:flex;align-items:flex-start;gap:10px;line-height:1.5;font-weight:500;}
        .warr{color:#086b5f;font-weight:700;flex-shrink:0;}
        .wnetwork{display:flex;flex-wrap:wrap;gap:6px;margin-top:12px;}
        .wntag{border:1px solid rgba(8,107,95,.3);padding:4px 12px;font-size:11px;color:#086b5f;background:rgba(8,107,95,.07);font-weight:600;}
        .wyp-italic{font-size:13px;color:#082030;line-height:1.7;border-left:2px solid #086b5f;padding-left:16px;font-style:italic;margin-bottom:20px;font-weight:500;}

        /* ── LIMITED ── */
        .limited{background:linear-gradient(135deg,#d4f5ee,#e4f6fb);border:1px solid rgba(8,107,95,.22);padding:40px;margin-top:0;}
        .limited-grid{display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:center;}
        .limited-pts{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:20px;}
        .limited-pt{border:1px solid rgba(8,107,95,.22);background:#fff;padding:14px;font-size:12px;color:#082030;display:flex;align-items:center;gap:8px;font-weight:700;}
        .limited-pt::before{content:'✓';color:#086b5f;font-weight:700;flex-shrink:0;}
        .limited-lbl{font-size:12px;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:#086b5f;margin-bottom:16px;}

        /* ── FAQ (dark bg via secalt) ── */
        .fql{margin-top:40px;display:flex;flex-direction:column;gap:1px;background:var(--border);border:1px solid var(--border);}
        .fqi{background:var(--card);}
        .fqq{width:100%;background:none;border:none;color:rgba(255,255,255,.92);padding:18px 26px;text-align:left;font-size:14px;font-weight:600;display:flex;justify-content:space-between;align-items:center;gap:16px;transition:color .2s;}
        .fqq:hover,.fqq.on{color:var(--teal3);}
        .fqic{color:var(--teal3);font-size:18px;flex-shrink:0;transition:transform .3s;}
        .fqa{font-size:13px;color:rgba(255,255,255,.78);line-height:1.7;padding:16px 26px 20px;border-top:1px solid var(--border);font-weight:400;}
        .secalt .lbl{color:var(--teal3);}
        .secalt .lbl::before{background:linear-gradient(90deg,transparent,var(--teal3));}
        .secalt .h2 .it{color:var(--teal3);}
        .secalt .h2{color:#fff;}
        .secalt .muted{color:rgba(255,255,255,.75);}

        /* ── FORM SECTION ── */
        .form-sec{background:#edfafa;padding:60px 0;}
        .form-inner{display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:start;}
        .form-lbl{font-size:10px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#086b5f;margin-bottom:12px;display:flex;align-items:center;gap:8px;}
        .form-lbl::before{content:'';height:1px;background:linear-gradient(90deg,transparent,#086b5f);width:30px;}
        .form-h2{font-size:clamp(1.6rem,3.5vw,2.5rem);font-weight:800;line-height:1.1;letter-spacing:-.025em;color:#061820;margin-bottom:14px;}
        .form-h2 span{font-family:'Instrument Serif',serif;font-style:italic;color:#086b5f;font-weight:400;}
        .form-desc{font-size:13px;color:#0a2030;line-height:1.7;margin-bottom:24px;font-weight:500;}
        .form-bullets{list-style:none;display:flex;flex-direction:column;gap:8px;}
        .form-bullets li{font-size:13px;color:#082030;display:flex;align-items:center;gap:8px;font-weight:600;}
        .form-bullets li::before{content:'✔';color:#086b5f;font-weight:700;flex-shrink:0;}
        .form-card{background:#fff;border:1px solid rgba(8,107,95,0.25);padding:36px;box-shadow:0 4px 20px rgba(8,107,95,0.1);}
        .form-card-title{font-size:1rem;font-weight:700;color:#061820;margin-bottom:20px;display:flex;align-items:center;gap:8px;}
        .fg{display:flex;flex-direction:column;gap:16px;}
        .fl{display:flex;flex-direction:column;gap:4px;}
        .fl label{font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#0a2030;}
        .fl label span{color:#dc2626;}
        .fi{border:1px solid rgba(8,107,95,0.3);background:#fff;padding:10px 14px;font-size:13px;color:#061820;outline:none;transition:border-color .2s,box-shadow .2s;width:100%;}
        .fi:focus{border-color:#086b5f;box-shadow:0 0 0 3px rgba(8,107,95,.12);}
        .fi::placeholder{color:#5a9ea0;}
        select.fi{appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23086b5f' fill='none' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center;padding-right:40px;}
        .fsubmit{background:linear-gradient(135deg,#086b5f,var(--blue2));border:none;color:#fff;padding:14px 28px;width:100%;font-size:13px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;transition:all .25s;box-shadow:0 4px 20px rgba(8,107,95,.25);}
        .fsubmit:hover{box-shadow:0 4px 32px rgba(8,107,95,.5);transform:translateY(-1px);}
        .fsubmit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .success{text-align:center;padding:40px 24px;}
        .success-icon{font-size:48px;margin-bottom:16px;}
        .success h3{font-size:1.3rem;font-weight:800;color:#061820;margin-bottom:8px;}
        .success p{font-size:13px;color:#0a2030;line-height:1.6;}

        /* ── CTA ── */
        .cta{position:relative;overflow:hidden;text-align:center;padding:100px 5%;background:var(--navy);}
        .ctabg{position:absolute;inset:0;background:radial-gradient(ellipse 70% 70% at 50% 50%,rgba(20,184,166,.1),transparent);pointer-events:none;}
        .ctalines{position:absolute;inset:0;pointer-events:none;opacity:.06;background-image:repeating-linear-gradient(0deg,transparent,transparent 79px,var(--teal) 80px),repeating-linear-gradient(90deg,transparent,transparent 79px,var(--teal) 80px);}
        .ctain{position:relative;max-width:720px;margin:0 auto;}
        .ctah{font-size:clamp(2.2rem,5vw,4rem);font-weight:800;line-height:1.05;letter-spacing:-.025em;margin-bottom:16px;color:#fff;}
        .ctah .it{font-family:'Instrument Serif',serif;font-style:italic;color:var(--teal3);font-weight:400;}
        .ctasub{font-size:0.95rem;color:rgba(255,255,255,.72);line-height:1.7;margin-bottom:18px;font-weight:300;}
        .ctatag{font-size:.85rem;color:var(--teal3);font-weight:600;margin-bottom:28px;}
        .ctan{margin-top:20px;font-size:10px;color:rgba(255,255,255,.3);letter-spacing:.07em;}

        /* ── FOOTER ── */
        .ftr{background:var(--card);border-top:1px solid var(--border2);padding:40px 5%;}
        .ftrin{max-width:1280px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;}
        .ftrb{display:flex;flex-direction:column;gap:4px;}
        .ftrn{font-size:15px;font-weight:800;color:var(--teal3);}
        .ftrs{font-size:11px;color:rgba(255,255,255,.6);}
        .ftrlinks{display:flex;gap:20px;flex-wrap:wrap;}
        .ftrlinks a{font-size:11px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.55);text-decoration:none;transition:color .2s;}
        .ftrlinks a:hover{color:var(--teal3);}
        .ftrcopy{font-size:10px;color:rgba(255,255,255,.3);}

        /* ── RESPONSIVE ── */
        @media(max-width:1024px){
          .nlinks,.ncta{display:none;}
          .hbg{display:flex;}
          .split{grid-template-columns:1fr;}
          .fail-split{grid-template-columns:1fr;gap:40px;}
          .sl{padding:40px 0 30px;}
          .sr{padding:30px 0 40px;}
          .sw{grid-template-columns:1fr;}
          .rg{grid-template-columns:repeat(2,1fr);}
          .stats{grid-template-columns:repeat(2,1fr);}
          .stat:nth-child(2){border-right:none;}
          .stat:nth-child(3){border-top:1px solid var(--border);}
          .wg{grid-template-columns:1fr;}
          .limited-grid{grid-template-columns:1fr;}
          .form-inner{grid-template-columns:1fr;}
          .plans{grid-template-columns:1fr;}
          .ftrin{flex-direction:column;align-items:flex-start;}
        }
        @media(max-width:768px){
          .rg{grid-template-columns:1fr;}
          .wcs{grid-template-columns:1fr;}
          .sdp{padding:28px;}
          .plan{padding:28px 20px;}
          .sec{padding:40px 0;}
          .sl,.sr{padding:30px 0;}
          .limited-pts{grid-template-columns:1fr;}
          .form-card{padding:24px;}
          .limited{padding:28px 20px;}
          .funding-tags{gap:8px;}
          .funding-tag{padding:6px 14px;font-size:11px;}
        }
        @media(max-width:480px){
          .stats{grid-template-columns:1fr 1fr;}
          .stat{padding:20px 12px;border-right:1px solid var(--border);border-bottom:1px solid var(--border);}
          .stat:nth-child(2n){border-right:none;}
          .pgrid{grid-template-columns:1fr;}
          .h1{font-size:2.4rem;}
          .funding-tags{gap:6px;}
          .funding-tag{padding:5px 12px;font-size:10px;}
        }
      `}</style>

      {/* ─── HERO ─── */}
      <section className="hero">
        <AuroraCanvas />
        <div className="hcont">
          <div className="eyebrow"><span className="edot"/><span className="etxt">Plan your roadmap for 2026-2027</span></div>
          <h1 className="h1">Build a <span className="it">Fund-Ready</span> Business</h1>
          <p className="htag">Structure · Execute · Fundraise</p>
          <p className="hdesc">
            Raising capital is not about presentations. It is about financial clarity, structured planning, compliance strength, and valuation readiness.
          </p>
          
          {/* Funding Services Tags */}
          <div className="funding-tags">
            {fundingServices.map((service, index) => (
              <span key={index} className="funding-tag">{service}</span>
            ))}
          </div>

          <div className="hbtns">
            <button className="btng" onClick={scrollToForm}>Apply for Capital Blueprint →</button>
            <button className="btno" onClick={scrollToForm}>Book Enterprise Consultation</button>
          </div>
        </div>
      </section>

      {/* ─── MARQUEE ─── */}
      <div className="mq">
        <div className="mqt">
          {[...Array(2)].map((_, r) =>
            ["Capital Structuring","Investor Readiness","EBITDA Modelling","Governance Audit","Valuation Logic","PE / VC Strategy","Use of Funds","Financial Projections","Compliance Architecture","Due Diligence Prep","Audit Readiness","Fundraising Support"].map((t,i) => (
              <div key={`${r}-${i}`} className="mqi"><div className="mqd"/>{t}</div>
            ))
          )}
        </div>
      </div>

      {/* ─── STATS ─── */}
      <div className="stats">
        {[{ n:500,s:"+",l:"Companies Assessed"},{n:200,s:"Cr+",l:"Capital Structured"},{n:12,s:"+",l:"Industries Served"},{n:95,s:"%",l:"Investor Readiness Rate"}].map((s,i)=>(
          <div key={i} className="stat">
            <div className="statn"><CountUp to={s.n} suffix={s.s}/></div>
            <div className="statl">{s.l}</div>
            <div className="statbar"/>
          </div>
        ))}
      </div>

      {/* ─── WHY FAIL + HOW DIFFERENT (SPLIT SECTION) ─── */}
      <section className="sec secw">
        <div className="wrap">
          <div className="fail-split">
            {/* LEFT HALF - Why Companies Fail (UNCHANGED) */}
            <Reveal className="fail-left">
              <div className="lbl">The Hard Truth</div>
              <h2 className="h2">Why Most Companies <span className="it">Fail</span><br/>to Raise Capital</h2>
              <p className="muted" style={{ maxWidth:560, marginTop:12 }}>
                Even profitable companies struggle because they lack the structured foundation investors require.
              </p>
              <div className="pgrid">
                {["Structured 2–3 year financial projections","Clear capital requirement planning","Valuation positioning","Governance & compliance structure","Audit readiness","Defined use of funds roadmap","Professional financial documentation"].map((t,i)=>(
                  <Reveal key={i} className="pcell" delay={i*55}>
                    <div className="px">✗</div>
                    <div className="pt">{t}</div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={500}>
                <div className="pfail">"Without financial structuring, funding discussions collapse."</div>
              </Reveal>
            </Reveal>

            {/* RIGHT HALF - How we're different (COMPLETELY RESTYLED) */}
            <Reveal className="fail-right" delay={150}>
              <h2 className="h2">How are we <span className="it">different</span> from other experts?</h2>
              
              <div className="qcard-right">
                <p className="qtxt-right">"This is not generic consulting. This is structured capital preparation."</p>
              </div>
              <div className="notgeneric-right">
                <strong>What makes this different?</strong>
                <p>We do not just prepare reports. We prepare businesses for capital — with the financial discipline, structured planning, and institutional-grade rigor that serious investors demand.</p>
              </div>

              <div className="tags-right">
                {["PE / VC Ready","EBITDA Modelling","Audit Prep","CFO Services","Valuation Logic","Use of Funds","Compliance","Investor Docs"].map(t=>(
                  <span key={t} className="tg-right">{t}</span>
                ))}
              </div>
              <div className="execution-quote">
                <p>"Execution is where we expertise."</p>
                <span>— AQT Direct Team</span>
              </div>

            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── PROGRAM SECTION (FULL WIDTH - REDESIGNED) ─── */}
      <section className="sec">
        <div className="wrap">
          <div className="program-full">
            <Reveal>
              <div className="lbl">Introducing</div>
              <h2 className="h2">AQT Capital Readiness<br/>&amp; <span className="it">Fundraising Program</span></h2>
              <p className="muted" style={{ marginTop:12, maxWidth:700 }}>
                A structured financial preparation framework designed for companies planning expansion, private equity investment, venture funding, or institutional capital.
              </p>
              
              {/* Redesigned points section */}
              <div className="program-points">
                <p className="program-points-label">We help you:</p>
                <ul className="program-points-grid">
                  {[
                    "Develop 2–3 year financial roadmap",
                    "Structure revenue & profitability projections",
                    "Identify correct funding route (PE / VC / Debt / Retail)",
                    "Strengthen compliance and governance",
                    "Prepare valuation logic",
                    "Build capital deployment strategy",
                    "Improve investor confidence"
                  ].map((point, i) => (
                    <li key={i} className="program-point-item">
                      <span className="program-point-icon">→</span>
                      <span className="program-point-text">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── REASONS ─── */}
      <section className="sec secw2">
        <div className="wrap">
          <Reveal>
            <div className="lbl">What We Fix</div>
            <h2 className="h2">Six Pillars of <span className="it">Capital Readiness</span></h2>
          </Reveal>
          <div className="rg">
            {[
              { icon:"📊", t:"Structured Projections", d:"2–3 year financial roadmap built to institutional standards that investors can scrutinise and trust." },
              { icon:"⚖️", t:"Valuation Readiness", d:"Clear valuation logic and positioning for credible, defensible investor conversations." },
              { icon:"🛡️", t:"Governance Strength", d:"Compliance architecture that passes thorough due diligence scrutiny without critical gaps." },
              { icon:"🗺️", t:"Use of Funds Roadmap", d:"Defined capital deployment strategy investors can map to milestones and expected returns." },
              { icon:"📁", t:"Audit Readiness", d:"Financial documentation prepared and organised to institutional audit quality standards." },
              { icon:"🎯", t:"Funding Route Clarity", d:"PE / VC / Debt / Retail — we identify the right capital path for your stage and sector." },
            ].map((r,i)=>(
              <Reveal key={i} className="rc" delay={i*70}>
                <span className="ri">{r.icon}</span>
                <div className="rt">{r.t}</div>
                <div className="rd">{r.d}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PLANS ─── */}
      <section className="sec">
        <div className="wrap">
          <Reveal>
            <div className="lbl">Our Two Strategic Plans</div>
            <h2 className="h2">Choose Your <span className="it">Readiness Path</span></h2>
            <p className="muted" style={{ maxWidth:520, marginTop:12 }}>Whether you need direction or full-spectrum execution support — there's a structured plan designed for your stage.</p>
          </Reveal>
          <div className="plans">

            {/* PLAN 1 */}
            <Reveal className="plan plan-s" delay={100}>
              <div className="ptag">🔹 Plan 1 – Capital Blueprint Plan</div>
              <div className="pname">Capital Blueprint Plan</div>
              <div className="pdesign">Designed for early and growth-stage companies seeking financial direction.</div>
              <div className="pprice"><span style={{textDecoration:'line-through',opacity:0.5,fontSize:'1.8rem',marginRight:'12px'}}><sup>₹</sup>1,00,000</span><sup>₹</sup>60,000</div>
              <div className="pnote">⏱ Delivery: 15 Days &nbsp;·&nbsp; Investment: ₹60,000</div>
              <div className="pwhat">What You Receive:</div>
              <ul className="pf">
                {["Financial health review","Revenue & EBITDA projection model (2–3 years)","Capital requirement assessment","Funding strategy recommendation","Cash flow planning","Financial gap analysis","Investor documentation checklist","Strategic roadmap summary document"].map((f,i)=>(
                  <li key={i}><span className="ck">•</span>{f}</li>
                ))}
              </ul>
              <div className="pideal">
                <div className="pideal-l">Ideal For:</div>
                <ul>
                  <li>SMEs preparing for expansion</li>
                  <li>Startups planning first institutional raise</li>
                  <li>Promoters needing structured clarity</li>
                </ul>
              </div>
              <button className="pb pb-p" onClick={scrollToForm}>👉 Apply for Capital Blueprint Plan</button>
            </Reveal>

            {/* PLAN 2 */}
            <Reveal className="plan plan-p" delay={200}>
              <div className="rib">Recommended</div>
              <div className="ptag">🔹 Plan 2 – Enterprise Capital Structuring Plan</div>
              <div className="pname">Enterprise Capital Structuring</div>
              <div className="pdesign">Designed for companies preparing for serious fundraising and institutional capital.</div>
              <div className="pprice"><span style={{textDecoration:'line-through',opacity:0.5,fontSize:'1.8rem',marginRight:'12px'}}><sup>₹</sup>5,00,000</span><sup>₹</sup>2,00,000</div>
              <div className="pnote">⏱ Delivery: 30 Days &nbsp;·&nbsp; Includes Everything in Plan 1 PLUS:</div>
              <div className="pwhat">Additional Features:</div>
              <ul className="pf">
                {["On-site company visit","Detailed financial audit assessment","Business model evaluation","Risk & compliance review","Valuation readiness analysis","Investor pitch financial structuring","Capital deployment plan","Hands-on execution guidance","Fundraising preparation support","Strategic capital introductions (subject to qualification)"].map((f,i)=>(
                  <li key={i}><span className="ck">•</span>{f}</li>
                ))}
              </ul>
              <div className="pideal">
                <div className="pideal-l">Ideal For:</div>
                <ul>
                  <li>Companies raising Seed / Series A / Growth Capital</li>
                  <li>Businesses seeking PE or strategic investors</li>
                  <li>Revenue-stage companies preparing structured expansion</li>
                </ul>
              </div>
              <button className="pb pb-p" onClick={scrollToForm}>👉 Book Enterprise Consultation</button>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ─── STEPS ─── */}
      <section className="sec secw">
        <div className="wrap">
          <Reveal>
            <div className="lbl">Our Structured Approach</div>
            <h2 className="h2">How We <span className="it">Work With You</span></h2>
          </Reveal>
          <div className="sw">
            <div className="snav">
              {steps.map((s,i)=>(
                <button key={i} className={`sb${activeStep===i?" on":""}`} onClick={()=>setActiveStep(i)}>
                  <span className="sn">{s.num}</span>{s.title}
                </button>
              ))}
            </div>
            <div className="sdp">
              <div className="sbig">{steps[activeStep].num}</div>
              <div className="sico">{steps[activeStep].icon}</div>
              <div className="stit">{steps[activeStep].title}</div>
              <div className="sdesc">{steps[activeStep].desc}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHO + WHY ─── */}
      <section className="sec">
        <div className="wrap">
          <div className="wg">
            <Reveal>
              <div className="lbl" style={{ color:"var(--blue)" }}>Who Should Apply</div>
              <h2 className="h2">Built for <span className="it">Growth-Stage</span><br/>Businesses</h2>
              <p style={{ marginTop:12, color:"var(--muted)", fontSize:"0.95rem", lineHeight:1.7, fontWeight:300 }}>
                This program is suitable for companies planning growth in the next 12–24 months.
              </p>
              <div className="wcs" style={{ marginTop:24 }}>
                {["Revenue-generating startups","MSMEs planning scale-up","Manufacturing & service companies","Tech-enabled businesses","Companies preparing institutional funding","Promoters seeking structured financial clarity"].map((w,i)=>(
                  <div key={i} style={{ border:"1px solid var(--border)", padding:14, fontSize:12, color:"rgba(255,255,255,.65)", display:"flex", alignItems:"center", gap:8, lineHeight:1.4, transition:"all .25s", background:"transparent" }}>
                    <div style={{ width:5, height:5, borderRadius:"50%", background:"var(--blue)", flexShrink:0 }}/>{w}
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={180}>
              <div style={{ background:"linear-gradient(160deg,var(--card),var(--cardb))", border:"1px solid var(--border)", padding:36, position:"relative", overflow:"hidden" }}>
                <div className="lbl" style={{ color:"var(--blue)" }}>Why Choose AQT</div>
                <h3 style={{ fontSize:"1.3rem", fontWeight:800, marginBottom:12, letterSpacing:"-.01em", color:"#fff" }}>
                  Strategic Capital <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:"italic", color:"var(--blue)", fontWeight:400 }}>Expertise</span>
                </h3>
                <p style={{ fontSize:13, color:"var(--muted)", lineHeight:1.7, borderLeft:"2px solid var(--blue)", paddingLeft:16, fontStyle:"italic", marginBottom:20 }}>
                  "We do not just prepare reports. We prepare businesses for capital."
                </p>
                <p style={{ fontSize:12, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:"var(--blue3)", marginBottom:10 }}>AQT Direct Limited works with:</p>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:12 }}>
                  {["AIFs","Venture Capital Funds","HNIs","Angel Investor Networks"].map(t=>(
                    <span key={t} style={{ border:"1px solid rgba(56,189,248,.2)", padding:"4px 12px", fontSize:11, color:"var(--blue3)" }}>{t}</span>
                  ))}
                </div>
                <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:10, marginTop:20 }}>
                  {["In association with HBF Direct Limited — a Core Investment Company & Venture Studio","Financial discipline & governance positioning","Capital structuring with hands-on execution support","Long-term scalability focused approach"].map((l,i)=>(
                    <li key={i} style={{ fontSize:13, color:"rgba(255,255,255,.7)", display:"flex", alignItems:"flex-start", gap:10, lineHeight:1.5 }}>
                      <span style={{ color:"var(--blue)", fontWeight:700, flexShrink:0 }}>→</span>{l}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── LIMITED INTAKE ─── */}
      <section className="sec secw2">
        <div className="wrap">
          <Reveal>
            <div className="limited">
              <div className="limited-grid">
                <div>
                  <div className="lbl" style={{ color:"#086b5f" }}>⚡ Limited Intake</div>
                  <h2 className="h2" style={{ fontSize:"clamp(1.6rem,3.2vw,2.4rem)" }}>We Onboard a <span className="it">Limited Number</span><br/>of Companies Per Month</h2>
                  <p className="muted" style={{ marginTop:12 }}>
                    To ensure execution quality and focused attention, we carefully review every application and accept only companies we can genuinely prepare for capital.
                  </p>
                </div>
                <div>
                  <p style={{ fontSize:12, fontWeight:700, letterSpacing:".15em", textTransform:"uppercase", color:"var(--blue2)", marginBottom:16 }}>Applications Reviewed Based On:</p>
                  <div className="limited-pts">
                    {["Revenue stage","Scalability","Capital requirement","Governance readiness"].map(t=>(
                      <div key={t} className="limited-pt">{t}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="sec secalt">
        <div className="wrap" style={{ maxWidth:860 }}>
          <Reveal style={{ textAlign:"center" }}>
            <div className="lbl" style={{ justifyContent:"center" }}>Common Questions</div>
            <h2 className="h2" style={{ textAlign:"center" }}>Frequently Asked <span className="it">Questions</span></h2>
          </Reveal>
          <div className="fql">
            {faqs.map((f,i)=>(
              <Reveal key={i} className="fqi" delay={i*60}>
                <button className={`fqq${openFaq===i?" on":""}`} onClick={()=>setOpenFaq(openFaq===i?null:i)}>
                  {f.q}
                  <span className="fqic" style={{ transform:openFaq===i?"rotate(45deg)":"none" }}>+</span>
                </button>
                {openFaq===i && <div className="fqa">{f.a}</div>}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CONTACT FORM ─── */}
      <section className="form-sec">
        <div className="wrap">
          <div className="form-inner">
            <Reveal>
              <div className="form-lbl">🔷 Capital Readiness Query Form</div>
              <h2 className="form-h2">Start Your <span>Capital Readiness</span> Journey</h2>
              <p className="form-desc">
                Fill in the form and our team will review your application. We onboard companies based on revenue stage, scalability, capital requirement, and governance readiness.
              </p>
              <ul className="form-bullets">
                <li>No obligation — just a structured conversation</li>
                <li>Response within 2 business days</li>
                <li>Completely confidential assessment</li>
                <li>Matched to the right plan for your stage</li>
              </ul>
            </Reveal>
            <Reveal delay={150}>
              <div className="form-card">
                {submitted ? (
                  <div className="success">
                    <div className="success-icon">✅</div>
                    <h3>Application Received</h3>
                    <p>Thank you for your interest in the AQT Capital Readiness Program. Our team will review your application and reach out within 2 business days.</p>
                  </div>
                ) : (
                  <>
                    <div className="form-card-title">
                      <span style={{ color:"var(--blue2)", fontSize:18 }}>1️⃣</span> Basic Information
                    </div>
                    <form className="fg" onSubmit={handleSubmit}>
                      <div className="fl">
                        <label>Full Name <span>*</span></label>
                        <input className="fi" type="text" placeholder="Your full name" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
                      </div>
                      <div className="fl">
                        <label>Designation <span>*</span></label>
                        <select className="fi" required value={form.designation} onChange={e=>setForm({...form,designation:e.target.value})}>
                          <option value="">Select your designation</option>
                          <option>Founder</option>
                          <option>Co-Founder</option>
                          <option>Director</option>
                          <option>CFO</option>
                          <option>CEO</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div className="fl">
                        <label>Company Name <span>*</span></label>
                        <input className="fi" type="text" placeholder="Your company name" required value={form.company} onChange={e=>setForm({...form,company:e.target.value})}/>
                      </div>
                      <div className="fl">
                        <label>Website (if any)</label>
                        <input className="fi" type="url" placeholder="https://yourwebsite.com" value={form.website} onChange={e=>setForm({...form,website:e.target.value})}/>
                      </div>
                      <div className="fl">
                        <label>Contact Number <span>*</span></label>
                        <input className="fi" type="tel" placeholder="+91 XXXXX XXXXX" required value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/>
                      </div>
                      <div className="fl">
                        <label>Official Email ID <span>*</span></label>
                        <input className="fi" type="email" placeholder="you@company.com" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
                      </div>
                      <button type="submit" className="fsubmit" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit Application →'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="cta">
        <div className="ctabg"/>
        <div className="ctalines"/>
        <Reveal className="ctain">
          <div className="lbl" style={{ justifyContent:"center", marginBottom:16 }}>Final Call to Action</div>
          <h2 className="ctah">Become <span className="it">Fund-Ready.</span><br/>Raise Capital with Structure.</h2>
          <p className="ctasub">Build Financial Clarity. Strengthen Governance. Approach Investors with Confidence.</p>
          <p className="ctatag">Book a Strategy Consultation Today.</p>
          <div className="hbtns">
            <button className="btng" style={{ padding:"14px 32px" }}>Schedule Call →</button>
            <button className="btno" style={{ padding:"14px 32px" }}>Request Proposal</button>
          </div>
          <p className="ctan">AQT Direct Limited · Building Fund-Ready Businesses · By AQT Direct Limited in strategic association with HBF Direct Limited</p>
        </Reveal>
      </section>

    </div>
    <Footer />
    </>
  );
}